import React from 'react';

import classNames from 'classnames';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons';
import { BREAKPOINTS, useMobileBreakpoint } from '@buildoutinc/blueprint-react/hooks/use-mobile';
import { DropdownMenu } from '@buildoutinc/blueprint-react/ui/DropdownMenu';
import { Avatar } from '@buildoutinc/blueprint-react/ui/Avatar';
import { Offcanvas } from '@buildoutinc/blueprint-react/ui/Offcanvas';
import { Separator } from '@buildoutinc/blueprint-react/ui/Separator';

import { PROFILE_ICONS } from './icons';
import { openSupport } from './support';
import { openWorkramp } from './workramp';
import type { OsNavProfileItem, OsNavUser } from './types';

function dataAttributes(item: OsNavProfileItem): Record<string, string> {
  const attributes: Record<string, string> = {};

  Object.entries(item.data ?? {}).forEach(([key, value]) => {
    attributes[`data-${key}`] = String(value);
  });

  return attributes;
}

const COOKIE_SETTINGS_PROPS = {
  'data-bs-toggle': 'modal',
  'data-bs-target': '#js-cookie-settings',
};

function itemProps(item: OsNavProfileItem, onNavigate?: () => void) {
  const shared = {
    className: item.linkClass,
    ...dataAttributes(item),
    onClick: onNavigate,
  };

  if (item.key === 'academy') {
    return {
      ...shared,
      href: item.path,
      onClick: (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        onNavigate?.();
        openWorkramp(String(item.data?.path ?? ''));
      },
    };
  }

  if (item.key === 'cookie_settings') return { ...shared, ...COOKIE_SETTINGS_PROPS };
  if (item.external) return { ...shared, href: item.path, rel: 'noopener', target: '_blank' };

  return { ...shared, href: item.path };
}

interface ItemsProps {
  items: OsNavProfileItem[];
  render: (item: OsNavProfileItem, content: React.ReactNode) => React.ReactNode;
}

function ProfileItems({ items, render }: ItemsProps) {
  return (
    <>
      {items.map(item => (
        <React.Fragment key={item.key}>
          {item.key === 'logout' && <Separator />}
          {render(
            item,
            <>
              <span className="sidebar-menu-link-icon">
                <FontAwesomeIcon icon={PROFILE_ICONS[item.key]} />
              </span>
              <span className="sidebar-menu-link-label">{item.label}</span>
            </>
          )}
        </React.Fragment>
      ))}

      <Separator />

      {render(
        { key: 'support', label: 'Support' },
        <>
          <span className="sidebar-menu-link-icon">
            <FontAwesomeIcon icon={faCircleQuestion} />
          </span>
          <span className="sidebar-menu-link-label">Support</span>
        </>
      )}
    </>
  );
}

interface ProfileMenuProps {
  items: OsNavProfileItem[];
  user: OsNavUser;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function ProfileMenu({ items, user, isOpen, onOpenChange }: ProfileMenuProps) {
  const isMobile = useMobileBreakpoint(BREAKPOINTS.md);

  const trigger = (
    <Avatar>
      <Avatar.Image alt={user.name} src={user.avatarUrl} />
      <Avatar.Fallback>{user.name.slice(0, 1)}</Avatar.Fallback>
    </Avatar>
  );

  if (isMobile) {
    return (
      <>
        <button
          aria-label="Account"
          className="os-nav__avatar-button"
          type="button"
          onClick={() => onOpenChange(true)}
        >
          {trigger}
        </button>

        <Offcanvas open={isOpen} onOpenChange={onOpenChange}>
          <Offcanvas.Content className="os-nav__panel" side="left">
            <Offcanvas.Header className="os-nav__panel-header">
              <span className="os-nav__panel-identity">
                <Avatar>
                  <Avatar.Image alt="" src={user.avatarUrl} />
                  <Avatar.Fallback>{user.name.slice(0, 1)}</Avatar.Fallback>
                </Avatar>
                <span className="d-flex flex-column">
                  <Offcanvas.Title>{user.name}</Offcanvas.Title>
                  {user.email && <span className="os-nav__panel-email">{user.email}</span>}
                </span>
              </span>
            </Offcanvas.Header>

            <Offcanvas.Body className="os-nav__panel-body">
              <ProfileItems
                items={items}
                render={(item, content) => {
                  const close = () => onOpenChange(false);

                  if (item.key === 'support') {
                    return (
                      <button
                        className="sidebar-menu-link"
                        type="button"
                        onClick={() => {
                          close();
                          openSupport();
                        }}
                      >
                        {content}
                      </button>
                    );
                  }

                  const props = itemProps(item, close);

                  return (
                    <a {...props} className={classNames('sidebar-menu-link', props.className)}>
                      {content}
                    </a>
                  );
                }}
              />
            </Offcanvas.Body>
          </Offcanvas.Content>
        </Offcanvas>
      </>
    );
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={onOpenChange}>
      <DropdownMenu.Trigger
        aria-label="Account"
        className="os-nav__avatar-button"
        render={<button type="button" />}
      >
        {trigger}
      </DropdownMenu.Trigger>

      <DropdownMenu.Content align="end" className="os-nav__profile-menu" sideOffset={14}>
        <ProfileItems
          items={items}
          render={(item, content) => item.key === 'support' ? (
            <DropdownMenu.Item onClick={openSupport}>{content}</DropdownMenu.Item>
          ) : (
            <DropdownMenu.Item render={<a {...itemProps(item)} />}>{content}</DropdownMenu.Item>
          )
          }
        />
      </DropdownMenu.Content>
    </DropdownMenu>
  );
}
